import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Calendar, Users, Loader2, Download, Quote, ExternalLink } from "lucide-react";

const fetchMinuta = (pageId) => api.get(`/notion/minuta/${pageId}`).then((r) => r.data);

function RichText({ items }) {
  if (!items || items.length === 0) return null;
  return (
    <>
      {items.map((t, i) => {
        const cls = [
          t.bold && "font-semibold",
          t.italic && "italic",
          t.underline && "underline",
          t.strike && "line-through",
          t.code && "font-mono text-[0.875em] bg-slate-100 px-1 rounded",
        ].filter(Boolean).join(" ");
        const content = <span className={cls}>{t.text}</span>;
        if (t.href) {
          return (
            <a key={i} href={t.href} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline">
              {content}
            </a>
          );
        }
        return <span key={i}>{content}</span>;
      })}
    </>
  );
}

function Block({ b }) {
  switch (b.type) {
    case "heading_1":
      return <h2 className="text-base font-semibold text-slate-900 mt-5 mb-2 font-display"><RichText items={b.text} /></h2>;
    case "heading_2":
      return <h3 className="text-sm font-semibold text-slate-900 mt-4 mb-1.5 font-display"><RichText items={b.text} /></h3>;
    case "heading_3":
      return <h4 className="text-xs font-semibold text-slate-800 mt-3 mb-1 uppercase tracking-wide"><RichText items={b.text} /></h4>;
    case "paragraph": {
      const empty = !(b.text || []).length;
      if (empty) return <div className="h-2" />;
      return <p className="text-sm text-slate-700 leading-relaxed my-1.5"><RichText items={b.text} /></p>;
    }
    case "quote":
      return (
        <blockquote className="border-l-2 border-slate-300 pl-3 my-2 text-sm text-slate-600 italic">
          <Quote className="inline h-3 w-3 mr-1 text-slate-400" /><RichText items={b.text} />
        </blockquote>
      );
    case "callout":
      return (
        <div className="bg-slate-50 border border-slate-200 rounded-md p-3 my-2 text-sm flex items-start gap-2">
          {b.emoji && <span>{b.emoji}</span>}
          <div className="text-slate-700"><RichText items={b.text} /></div>
        </div>
      );
    case "bulleted_list_item":
      return (
        <div className="flex items-start gap-2 my-1 ml-3">
          <span className="text-slate-400 mt-1">•</span>
          <span className="text-sm text-slate-700 leading-relaxed flex-1"><RichText items={b.text} /></span>
        </div>
      );
    case "numbered_list_item":
      return (
        <div className="flex items-start gap-2 my-1 ml-3">
          <span className="text-slate-400 font-mono text-xs mt-1">1.</span>
          <span className="text-sm text-slate-700 leading-relaxed flex-1"><RichText items={b.text} /></span>
        </div>
      );
    case "to_do":
      return (
        <div className="flex items-start gap-2 my-1 ml-3 text-sm">
          <input type="checkbox" checked={b.checked} readOnly className="mt-1" />
          <span className={b.checked ? "line-through text-slate-400" : "text-slate-700"}><RichText items={b.text} /></span>
        </div>
      );
    case "toggle":
      return (
        <details className="my-1.5 text-sm">
          <summary className="cursor-pointer text-slate-700 font-medium"><RichText items={b.text} /></summary>
          <div className="ml-3 mt-1.5 text-slate-500 text-xs italic">(Expandir en Notion para ver detalle)</div>
        </details>
      );
    case "divider":
      return <hr className="my-3 border-slate-200" />;
    case "file":
    case "pdf":
      return (
        <a href={b.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm text-indigo-600 hover:underline my-2 border border-indigo-100 bg-indigo-50/30 rounded-md px-3 py-2">
          <Download className="h-3.5 w-3.5" />
          <span>{b.name || "Archivo"}</span>
        </a>
      );
    case "image":
      return (
        <figure className="my-3">
          <img src={b.url} alt="" className="rounded-md max-w-full border border-slate-200" />
          {(b.caption || []).length > 0 && (
            <figcaption className="text-[11px] text-slate-500 italic mt-1"><RichText items={b.caption} /></figcaption>
          )}
        </figure>
      );
    case "embed":
    case "bookmark":
    case "link_preview":
      return (
        <a href={b.url} target="_blank" rel="noreferrer" className="block my-2 border border-slate-200 rounded-md p-2.5 hover:bg-slate-50 transition">
          <div className="flex items-center gap-2 text-xs text-indigo-600">
            <ExternalLink className="h-3 w-3" />
            <span className="truncate">{b.url}</span>
          </div>
          {(b.text || []).length > 0 && <div className="text-xs text-slate-600 mt-1"><RichText items={b.text} /></div>}
        </a>
      );
    case "code":
      return (
        <pre className="bg-slate-900 text-slate-100 rounded-md p-3 my-2 text-xs overflow-x-auto">
          <code><RichText items={b.text} /></code>
        </pre>
      );
    default:
      return (b.text || []).length > 0 ? <p className="text-sm text-slate-600 my-1"><RichText items={b.text} /></p> : null;
  }
}

function fmtFullDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("es-AR", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

export default function MinutaDrawer({ open, onOpenChange, minuta }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["notion-minuta", minuta?.id],
    queryFn: () => fetchMinuta(minuta.id),
    enabled: !!(open && minuta?.id),
    staleTime: 5 * 60 * 1000,
  });

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto" data-testid="minuta-drawer">
        <SheetHeader className="border-b border-slate-100 pb-3 mb-4">
          <SheetTitle className="font-display text-base text-slate-900 pr-8 text-left">
            {minuta?.title || "Minuta"}
          </SheetTitle>
          <SheetDescription className="text-left">
            <div className="flex items-center gap-3 mt-1 text-xs text-slate-500 flex-wrap">
              {(data?.date || minuta?.date) && (
                <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> {fmtFullDate(data?.date || minuta?.date)}</span>
              )}
              {(data?.participants || []).length > 0 && (
                <span className="inline-flex items-center gap-1">
                  <Users className="h-3 w-3" />
                  {data.participants.join(", ")}
                </span>
              )}
            </div>
          </SheetDescription>
        </SheetHeader>

        {isLoading && (
          <div className="py-10 text-center text-sm text-slate-500 flex items-center justify-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Cargando minuta...
          </div>
        )}
        {error && (
          <div className="py-10 text-center text-sm text-rose-600">No se pudo cargar la minuta.</div>
        )}
        {data && !isLoading && (
          <div className="space-y-1 pb-6" data-testid="minuta-content">
            {(data.blocks || []).length === 0 ? (
              <div className="text-sm text-slate-500 italic py-6 text-center">Esta minuta no tiene contenido visible (se excluyen Accionables y Comentarios).</div>
            ) : (
              data.blocks.map((b, i) => <Block key={b.id || i} b={b} />)
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
